import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../pages/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';

const NAV  = '#16213E';
const GOLD = '#D4AF37';

const navLinks = [
  { path: '/',            label: 'Home' },
  { path: '/about',       label: 'About' },
  { path: '/vaikhanasam', label: 'Vaikhanasam' },
  { path: '/events',      label: 'Events' },
];

/* ── Drawer row helper ──────────────────────────────────────────── */
const DrawerRow = ({ to, icon, label, active, onClick }) => {
  const base = {
    fontFamily: 'Inter, sans-serif',
    color: active ? GOLD : 'rgba(255,255,255,0.72)',
    background: active ? 'rgba(212,175,55,0.1)' : 'transparent',
    borderLeft: active ? `3px solid ${GOLD}` : '3px solid transparent',
    paddingLeft: active ? '1.1rem' : '0.75rem',
    borderRadius: '12px',
    transition: 'background 0.15s, padding-left 0.15s',
    display: 'flex', alignItems: 'center', gap: '0.85rem',
    padding: `0.75rem ${active ? '1.1rem' : '0.75rem'}`,
    marginBottom: '2px',
    textDecoration: 'none',
    fontSize: '0.9rem',
    fontWeight: 500,
    minHeight: '44px',
  };
  if (to) return <Link to={to} style={base}><span style={{ fontSize: '1rem' }}>{icon}</span>{label}</Link>;
  return <button onClick={onClick} style={{ ...base, width: '100%', border: 'none', cursor: 'pointer', textAlign: 'left', background: 'transparent' }}><span style={{ fontSize: '1rem' }}>{icon}</span>{label}</button>;
};

function Navbar() {
  const { isAuthenticated, logout, role } = useAuth();
  const location  = useLocation();
  const navigate  = useNavigate();
  const dropRef   = useRef(null);

  const [userEmail,  setUserEmail]  = useState('');
  const [userName,   setUserName]   = useState('');
  const [dropOpen,   setDropOpen]   = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled,   setScrolled]   = useState(false);

  useEffect(() => {
    const token   = localStorage.getItem('vrvToken');
    const email   = localStorage.getItem('vrvEmail');
    const name    = localStorage.getItem('vrvName')    || '';
    const surname = localStorage.getItem('vrvSurname') || '';
    if (token && email) { setUserEmail(email); setUserName(`${name} ${surname}`.trim()); }
    else { setUserEmail(''); setUserName(''); }
  }, [location.pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const outside = (e) => { if (dropRef.current && !dropRef.current.contains(e.target)) setDropOpen(false); };
    document.addEventListener('mousedown', outside);
    return () => document.removeEventListener('mousedown', outside);
  }, []);

  useEffect(() => {
    const handler = () => {
      const name    = localStorage.getItem('vrvName')    || '';
      const surname = localStorage.getItem('vrvSurname') || '';
      setUserName(`${name} ${surname}`.trim());
    };
    window.addEventListener('vrv-profile-updated', handler);
    return () => window.removeEventListener('vrv-profile-updated', handler);
  }, []);

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  const handleLogout = () => { logout(); setDropOpen(false); setMobileOpen(false); navigate('/'); };
  const isActive     = (path) => location.pathname === path;
  const initials     = (userName || userEmail).charAt(0).toUpperCase();
  const isAdmin      = (role || '').split(',').map(s => s.trim().toUpperCase()).includes('ADMIN');
  const hasContent   = (role || '').split(',').map(s => s.trim().toUpperCase()).some(r => ['ADMIN','CONTENT-CREATOR','CONTENT-APPROVER'].includes(r));

  return (
    <>
      {/* ══ TOP NAV ══════════════════════════════════════════════ */}
      <nav
        className="fixed top-0 left-0 w-full z-50 transition-all duration-300"
        style={{
          height: '64px',
          backdropFilter:       scrolled ? 'blur(14px)' : 'blur(8px)',
          WebkitBackdropFilter: scrolled ? 'blur(14px)' : 'blur(8px)',
          background:    scrolled ? 'rgba(22,33,62,0.92)' : 'rgba(15,23,42,0.85)',
          borderBottom:  '1px solid rgba(212,175,55,0.12)',
          boxShadow:     scrolled ? '0 4px 30px rgba(0,0,0,0.2)' : 'none',
        }}
      >
        <div className="vrv-container h-full flex items-center justify-between">

          {/* Left: Logo */}
          <div className="flex items-center gap-3">
            <Link to="/" className="flex items-center gap-2.5 no-underline">
              <img src="/images/VRV.png" alt="VRV" className="h-10 w-10 object-contain"
                style={{ filter: 'drop-shadow(0 0 6px rgba(212,175,55,0.5))' }} />
              {/* Desktop: full name · Mobile: abbreviated */}
              <span className="hidden sm:block font-bold text-sm tracking-wide"
                style={{ fontFamily: 'Poppins, sans-serif', color: 'rgba(255,255,255,0.9)' }}>
                Vaikhanasa Raksha Vahini
              </span>
              <span className="block sm:hidden font-bold text-xs tracking-wide"
                style={{ fontFamily: 'Poppins, sans-serif', color: 'rgba(255,255,255,0.9)' }}>
                VRV
              </span>
            </Link>
          </div>

          {/* Center: Nav links — desktop only */}
          <div className="hidden md:flex items-center gap-1">
            {navLinks.map((link) => (
              <Link key={link.path} to={link.path}
                className={`relative px-4 py-2 rounded-xl text-xs font-semibold tracking-wide transition-all duration-200 ${isActive(link.path) ? 'nav-active-glow' : ''}`}
                style={{
                  fontFamily: 'Poppins, sans-serif',
                  color:      isActive(link.path) ? GOLD : 'rgba(255,255,255,0.65)',
                  background: isActive(link.path) ? 'rgba(212,175,55,0.1)' : 'transparent',
                }}
                onMouseEnter={e => { if (!isActive(link.path)) { e.currentTarget.style.color = 'white'; e.currentTarget.style.background = 'rgba(255,255,255,0.07)'; } }}
                onMouseLeave={e => { if (!isActive(link.path)) { e.currentTarget.style.color = 'rgba(255,255,255,0.65)'; e.currentTarget.style.background = 'transparent'; } }}>
                {link.label}
                {isActive(link.path) && (
                  <motion.span layoutId="nav-indicator" className="absolute bottom-0.5 left-4 right-4 h-0.5 rounded-full"
                    style={{ background: `linear-gradient(90deg, transparent, ${GOLD}, transparent)`, boxShadow: `0 0 8px ${GOLD}` }} />
                )}
              </Link>
            ))}
          </div>

          {/* Right: Auth — desktop only */}
          <div className="hidden md:flex items-center gap-3">
            {!isAuthenticated ? (
              <>
                <Link to="/login"
                  className="px-4 py-2 text-xs font-semibold rounded-xl transition-all duration-200"
                  style={{ fontFamily: 'Poppins, sans-serif', color: 'rgba(255,255,255,0.7)' }}
                  onMouseEnter={e => e.currentTarget.style.color = 'white'}
                  onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.7)'}>
                  Sign In
                </Link>
                <Link to="/signup" className="btn-gold text-xs px-5 py-2" style={{ minHeight: '36px', borderRadius: '10px' }}>
                  Join Us
                </Link>
              </>
            ) : (
              <div ref={dropRef} className="relative">
                <button onClick={() => setDropOpen(p => !p)}
                  className="flex items-center gap-2.5 px-2 py-1.5 rounded-xl transition-all duration-200"
                  style={{ background: dropOpen ? 'rgba(255,255,255,0.1)' : 'transparent' }}
                  onMouseEnter={e => { if (!dropOpen) e.currentTarget.style.background = 'rgba(255,255,255,0.07)'; }}
                  onMouseLeave={e => { if (!dropOpen) e.currentTarget.style.background = 'transparent'; }}>
                  <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
                    style={{ background: `linear-gradient(135deg, ${GOLD}, #B9932F)`, color: '#0F172A', boxShadow: '0 0 12px rgba(212,175,55,0.35)' }}>
                    {initials}
                  </div>
                  <div className="flex flex-col items-start leading-tight">
                    {userName && <span className="text-xs font-semibold text-white" style={{ fontFamily: 'Poppins, sans-serif' }}>{userName}</span>}
                    <span className="text-[10px]" style={{ color: 'rgba(255,255,255,0.45)' }}>{userEmail}</span>
                  </div>
                  <svg className={`w-3 h-3 transition-transform duration-200 ${dropOpen ? 'rotate-180' : ''}`}
                    style={{ color: 'rgba(255,255,255,0.5)' }} fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
                <AnimatePresence>
                  {dropOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: -6, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -6, scale: 0.95 }}
                      transition={{ duration: 0.12 }}
                      className="absolute right-0 mt-1.5 rounded-xl overflow-hidden"
                      style={{ background: 'rgba(22,33,62,0.95)', border: '1px solid rgba(212,175,55,0.15)', boxShadow: '0 8px 24px rgba(0,0,0,0.3)', backdropFilter: 'blur(12px)' }}>
                      <button onClick={handleLogout}
                        className="flex items-center gap-2 px-4 py-2 text-[11px] font-semibold tracking-wide transition-colors whitespace-nowrap"
                        style={{ fontFamily: 'Poppins, sans-serif', color: 'rgba(255,255,255,0.7)' }}
                        onMouseEnter={e => { e.currentTarget.style.color = '#EF4444'; e.currentTarget.style.background = 'rgba(239,68,68,0.1)'; }}
                        onMouseLeave={e => { e.currentTarget.style.color = 'rgba(255,255,255,0.7)'; e.currentTarget.style.background = 'transparent'; }}>
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                        </svg>
                        Sign Out
                      </button>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )}
          </div>

          {/* Mobile: Hamburger — always shows ≡ */}
          <button
            onClick={() => setMobileOpen(p => !p)}
            className="md:hidden flex items-center justify-center w-10 h-10 rounded-xl transition-colors"
            style={{ color: 'rgba(255,255,255,0.85)', background: mobileOpen ? 'rgba(212,175,55,0.15)' : 'transparent' }}
            aria-label="Open menu"
            aria-expanded={mobileOpen}>
            <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
        </div>
      </nav>

      {/* ══ MOBILE ACCOUNT / SUPPORT DRAWER ═════════════════════ */}
      <AnimatePresence>
        {mobileOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              transition={{ duration: 0.22 }}
              className="fixed inset-0 z-40 md:hidden"
              style={{ background: 'rgba(8,15,35,0.65)', backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)' }}
              onClick={() => setMobileOpen(false)}
              aria-hidden="true"
            />

            {/* Drawer panel */}
            <motion.div
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              transition={{ duration: 0.28, ease: [0.25, 0.46, 0.45, 0.94] }}
              className="fixed top-0 left-0 bottom-0 z-50 md:hidden"
              style={{
                width: '17rem',
                background: `linear-gradient(175deg, ${NAV} 0%, #0B1428 100%)`,
                borderRight: '1px solid rgba(212,175,55,0.18)',
                borderRadius: '0 20px 20px 0',
                boxShadow: '6px 0 48px rgba(0,0,0,0.55)',
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
              }}
              role="dialog"
              aria-modal="true"
              aria-label="Account menu"
            >
              {/* ── Drawer header ──────────────────────────────── */}
              <div style={{
                padding: '0 1.25rem',
                height: '64px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                borderBottom: '1px solid rgba(212,175,55,0.1)',
                flexShrink: 0,
              }}>
                <div className="flex items-center gap-2.5">
                  <img src="/images/VRV.png" alt="VRV" style={{ width: 36, height: 36, objectFit: 'contain', filter: 'drop-shadow(0 0 6px rgba(212,175,55,0.5))' }} />
                  <span style={{ fontFamily: 'Poppins, sans-serif', fontWeight: 700, fontSize: '0.82rem', color: 'rgba(255,255,255,0.92)' }}>
                    Vaikhanasa Raksha Vahini
                  </span>
                </div>
                <button
                  onClick={() => setMobileOpen(false)}
                  style={{ width: 32, height: 32, borderRadius: '50%', background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0 }}
                  aria-label="Close menu">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round">
                    <path d="M18 6L6 18M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* ── Scrollable body ────────────────────────────── */}
              <div style={{ flex: 1, overflowY: 'auto', padding: '1rem 1rem 0.5rem', minHeight: 0 }}>

                {isAuthenticated ? (
                  <>
                    {/* Premium profile card */}
                    <div style={{
                      background: 'linear-gradient(135deg, rgba(212,175,55,0.12) 0%, rgba(212,175,55,0.04) 100%)',
                      border: '1px solid rgba(212,175,55,0.22)',
                      borderRadius: '16px',
                      padding: '1.1rem 1rem',
                      marginBottom: '1.25rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.85rem',
                    }}>
                      <div style={{
                        width: 48, height: 48, borderRadius: '50%', flexShrink: 0,
                        background: `linear-gradient(135deg, ${GOLD}, #B9932F)`,
                        color: '#0F172A', fontFamily: 'Poppins, sans-serif',
                        fontWeight: 800, fontSize: '1.1rem',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        boxShadow: '0 0 16px rgba(212,175,55,0.35)',
                      }}>
                        {initials}
                      </div>
                      <div style={{ minWidth: 0 }}>
                        {userName && (
                          <p style={{ fontFamily: 'Poppins, sans-serif', fontWeight: 700, fontSize: '0.9rem', color: '#fff', marginBottom: '0.15rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {userName}
                          </p>
                        )}
                        <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '0.72rem', color: 'rgba(255,255,255,0.45)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {userEmail}
                        </p>
                      </div>
                    </div>

                    {/* Account section */}
                    <p style={{ fontFamily: 'Poppins, sans-serif', fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase', color: 'rgba(212,175,55,0.55)', marginBottom: '0.5rem', paddingLeft: '0.75rem' }}>
                      My Account
                    </p>
                    <DrawerRow to="/profile"    icon="👤" label="My Profile"   active={isActive('/profile')} />
                    <DrawerRow to="/donate"     icon="💛" label="My Donations" active={isActive('/donate')} />
                    <DrawerRow to="/articles"   icon="📖" label="My Articles"  active={isActive('/articles')} />
                    <DrawerRow to="/youtube"    icon="▶️"  label="My Videos"   active={isActive('/youtube')} />
                    {hasContent && <DrawerRow to="/content-management" icon="✏️" label="Content Tools" active={isActive('/content-management')} />}
                    {isAdmin    && <DrawerRow to="/admin" icon="⚙️" label="Admin Panel" active={isActive('/admin')} />}

                    {/* Divider */}
                    <div style={{ height: '1px', background: 'rgba(212,175,55,0.12)', margin: '0.85rem 0' }} />

                    {/* Support section */}
                    <p style={{ fontFamily: 'Poppins, sans-serif', fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase', color: 'rgba(212,175,55,0.55)', marginBottom: '0.5rem', paddingLeft: '0.75rem' }}>
                      Explore
                    </p>
                    <DrawerRow to="/about"       icon="🕉️" label="About VRV"    active={isActive('/about')} />
                    <DrawerRow to="/vaikhanasam" icon="📜" label="Vaikhanasam"  active={isActive('/vaikhanasam')} />

                    {/* Sign Out — always at bottom of scroll */}
                    <div style={{ height: '1px', background: 'rgba(212,175,55,0.12)', margin: '1rem 0 0.75rem' }} />
                    <button onClick={handleLogout}
                      style={{
                        width: '100%', display: 'flex', alignItems: 'center', gap: '0.75rem',
                        padding: '0.75rem 1rem', borderRadius: '12px', marginBottom: '1rem',
                        background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.18)',
                        color: '#F87171', fontFamily: 'Inter, sans-serif', fontSize: '0.88rem',
                        fontWeight: 600, cursor: 'pointer',
                      }}
                      onTouchStart={e => e.currentTarget.style.background = 'rgba(239,68,68,0.18)'}
                      onTouchEnd={e => e.currentTarget.style.background = 'rgba(239,68,68,0.08)'}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                        <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/>
                      </svg>
                      Sign Out
                    </button>
                  </>
                ) : (
                  <>
                    {/* Guest — explore links */}
                    <p style={{ fontFamily: 'Poppins, sans-serif', fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase', color: 'rgba(212,175,55,0.55)', marginBottom: '0.5rem', paddingLeft: '0.75rem' }}>
                      Explore
                    </p>
                    <DrawerRow to="/about"       icon="🕉️" label="About VRV"   active={isActive('/about')} />
                    <DrawerRow to="/vaikhanasam" icon="📜" label="Vaikhanasam" active={isActive('/vaikhanasam')} />

                    {/* Divider */}
                    <div style={{ height: '1px', background: 'rgba(212,175,55,0.12)', margin: '1rem 0' }} />

                    {/* Auth CTAs for guest */}
                    <Link to="/login"
                      style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', padding: '0.75rem', borderRadius: '12px', border: '1.5px solid rgba(255,255,255,0.2)', color: 'rgba(255,255,255,0.82)', fontFamily: 'Poppins, sans-serif', fontSize: '0.82rem', fontWeight: 700, textDecoration: 'none', marginBottom: '0.6rem', transition: 'border-color 0.2s' }}>
                      Sign In
                    </Link>
                    <Link to="/signup"
                      style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0.75rem', borderRadius: '12px', background: `linear-gradient(135deg, ${GOLD}, #B9932F)`, color: '#0F172A', fontFamily: 'Poppins, sans-serif', fontSize: '0.82rem', fontWeight: 700, textDecoration: 'none', boxShadow: '0 4px 16px rgba(212,175,55,0.3)' }}>
                      Join the Community
                    </Link>
                  </>
                )}
              </div>

              {/* ── Drawer footer: Sign Out ─────────────────────── */}
              {isAuthenticated && (
                <div style={{ padding: '0.85rem 1rem', borderTop: '1px solid rgba(212,175,55,0.1)', flexShrink: 0 }}>
                  <button onClick={handleLogout}
                    style={{
                      width: '100%', display: 'flex', alignItems: 'center', gap: '0.75rem',
                      padding: '0.75rem 1rem', borderRadius: '12px',
                      background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.15)',
                      color: '#F87171', fontFamily: 'Inter, sans-serif', fontSize: '0.88rem',
                      fontWeight: 600, cursor: 'pointer', transition: 'background 0.15s',
                    }}
                    onTouchStart={e => e.currentTarget.style.background = 'rgba(239,68,68,0.15)'}
                    onTouchEnd={e => e.currentTarget.style.background = 'rgba(239,68,68,0.08)'}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                      <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4" /><polyline points="16 17 21 12 16 7" /><line x1="21" y1="12" x2="9" y2="12" />
                    </svg>
                    Sign Out
                  </button>
                </div>
              )}
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}

export default Navbar;
